import 'core-js/modules/es.array.iterator.js';
import 'core-js/modules/web.dom-collections.iterator.js';
import 'core-js/modules/es.object.assign.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Card, Stack, Text, TextField, Select, MenuItem } from '@hub-la/design-system';
import { Autocomplete } from '@mui/material';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import NumberFormat from 'react-number-format';
import { BRAZILIAN_BANKS } from '../../domain/constants/brazilian-banks.js';
import { BankAccountType } from '../../domain/enums/bank-account.js';
import { useField } from '../hooks/use-field.js';

const BankAccountForm = () => {
  var _a;
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue
  } = useFormikContext();
  const {
    hasError,
    getErrorText
  } = useField();
  const {
    t
  } = useTranslation();
  const needsAgencyDigit = () => {
    var _a;
    return ['001', '041', '237', '633'].includes((_a = values.bankAccount.bankNumber) !== null && _a !== void 0 ? _a : '');
  };
  const shouldAcceptTextAtAccountCheckNumber = () => {
    var _a;
    return ['001', '237'].includes((_a = values.bankAccount.bankNumber) !== null && _a !== void 0 ? _a : '');
  };
  const shouldAcceptMoreDigitsAtAccountCheckNumber = () => {
    var _a;
    return ['752'].includes((_a = values.bankAccount.bankNumber) !== null && _a !== void 0 ? _a : '');
  };
  return jsx(Card, Object.assign({
    hasHover: false,
    variant: "filled"
  }, {
    children: jsxs(Stack, Object.assign({
      direction: "column",
      spacing: 4
    }, {
      children: [jsxs(Stack, Object.assign({
        direction: "column",
        spacing: 2
      }, {
        children: [jsx(Text, {
          children: t('accountVerification.form.bankAccount.title')
        }), jsx(Text, Object.assign({
          variant: "body2"
        }, {
          children: t('accountVerification.form.bankAccount.subtitle')
        }))]
      })), jsxs(Stack, Object.assign({
        direction: "column",
        spacing: 4
      }, {
        children: [jsx(Autocomplete, {
          value: (_a = BRAZILIAN_BANKS.find(bank => bank.code === values.bankAccount.bankNumber)) !== null && _a !== void 0 ? _a : null,
          onChange: (_e, value) => setFieldValue('bankAccount.bankNumber', value === null || value === void 0 ? void 0 : value.code),
          fullWidth: true,
          size: "medium",
          getOptionLabel: option => option.code + ' - ' + option.name,
          renderInput: params => jsx(TextField, Object.assign({}, params, {
            error: hasError('bankAccount.bankNumber'),
            helperText: getErrorText('bankAccount.bankNumber'),
            label: t('accountVerification.form.bankAccount.bank')
          })),
          options: BRAZILIAN_BANKS
        }), jsxs(Stack, Object.assign({
          direction: "row",
          gap: 3
        }, {
          children: [jsx(NumberFormat, {
            id: "bankAccount.agencyNumber",
            type: "tel" // numbers only
            ,
            value: values.bankAccount.agencyNumber,
            error: hasError('bankAccount.agencyNumber') || hasError('bankAccount.agencyCheckNumber'),
            helperText: getErrorText('bankAccount.agencyNumber') || getErrorText('bankAccount.agencyCheckNumber'),
            onBlur: handleBlur,
            onChange: handleChange,
            size: "normal",
            label: t('accountVerification.form.bankAccount.agency'),
            customInput: TextField,
            fullWidth: true,
            allowNegative: false
          }), needsAgencyDigit() && jsx(TextField, {
            id: "bankAccount.agencyCheckNumber",
            type: "text",
            value: values.bankAccount.agencyCheckNumber,
            error: hasError('bankAccount.agencyNumber') || hasError('bankAccount.agencyCheckNumber'),
            onBlur: handleBlur,
            onChange: handleChange,
            inputProps: {
              maxLength: 1
            },
            size: "normal",
            label: t('accountVerification.form.bankAccount.agencyDigit'),
            fullWidth: true
          })]
        })), jsx(Select, Object.assign({
          onChange: e => setFieldValue('bankAccount.accountType', e.target.value),
          value: values.bankAccount.accountType,
          fullWidth: true,
          size: "normal",
          label: t('accountVerification.form.bankAccount.accountType')
        }, {
          children: Object.values(BankAccountType).filter(x => typeof x === 'number').map(type => jsx(MenuItem, Object.assign({
            value: type
          }, {
            children: t(`accountVerification.form.bankAccount.types.${type}`)
          }), type))
        })), jsxs(Stack, Object.assign({
          direction: "row",
          gap: 3
        }, {
          children: [jsx(NumberFormat, {
            id: "bankAccount.accountNumber",
            type: "tel" // numbers only
            ,
            value: values.bankAccount.accountNumber,
            error: hasError('bankAccount.accountNumber') || hasError('bankAccount.accountCheckNumber'),
            helperText: getErrorText('bankAccount.accountNumber') || getErrorText('bankAccount.accountCheckNumber'),
            onBlur: handleBlur,
            onChange: handleChange,
            size: "normal",
            label: t('accountVerification.form.bankAccount.account'),
            customInput: TextField,
            fullWidth: true,
            allowNegative: false
          }), shouldAcceptTextAtAccountCheckNumber() ? jsx(TextField, {
            id: "bankAccount.accountCheckNumber",
            type: "text",
            value: values.bankAccount.accountCheckNumber,
            error: hasError('account') || hasError('bankAccount.accountCheckNumber'),
            onBlur: handleBlur,
            onChange: handleChange,
            inputProps: {
              maxLength: 1
            },
            size: "normal",
            label: t('accountVerification.form.bankAccount.accountDigit'),
            fullWidth: true
          }) : jsx(NumberFormat, {
            id: "bankAccount.accountCheckNumber",
            type: "tel" // numbers only
            ,
            value: values.bankAccount.accountCheckNumber,
            error: hasError('account') || hasError('bankAccount.accountCheckNumber'),
            onBlur: handleBlur,
            onChange: handleChange,
            inputProps: {
              maxLength: shouldAcceptMoreDigitsAtAccountCheckNumber() ? 3 : 1
            },
            size: "normal",
            label: t('accountVerification.form.bankAccount.accountDigit'),
            customInput: TextField,
            fullWidth: true,
            allowNegative: false
          })]
        }))]
      }))]
    }))
  }));
};

export { BankAccountForm };
